import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { UserFileRead, ReductoJobSafeRead, UserFileAssetRead } from '../schemas'

// ============================================================================
// Types
// ============================================================================

/**
 * Client-only upload status - tracked by frontend during upload
 */
export type ClientUploadStatus = 'validating' | 'queued' | 'initializing' | 'uploading' | 'uploaded' | 'error' | 'unsupported_type'

/**
 * Client-only state - exists during and after upload phase
 * This is data tracked by the frontend, not from the backend API
 */
export interface ClientFileState {
  // Identity (client-generated)
  uppyFileId: string
  clientTmpId: string
  filename: string
  fileSize: number
  folderId: string

  // Server-assigned (from init-upload, before full backend data)
  serverFileId?: string
  storagePath?: string

  // Upload progress (client-tracked)
  uploadStatus: ClientUploadStatus
  uploadProgress: number // 0-100
  uploadError?: string
}

/**
 * Backend state - received from GET /api/v1/user-files/ endpoint
 * Stored directly without transformation, mirrors backend schemas
 */
export interface BackendFileState {
  record: UserFileRead
  reductoJob: ReductoJobSafeRead | null
  assets: UserFileAssetRead[]
}

/**
 * Combined file state for a single file
 * Two-phase: starts client-only, gets enriched with backend data after upload
 */
export interface FileUploadState {
  // Client-only state (always present)
  client: ClientFileState

  // Backend state (null until first API response after upload)
  backend: BackendFileState | null
}

export interface UppyFileUploadState {
  files: Record<string, FileUploadState> // keyed by clientTmpId
  /**
   * Upload drawer UI state — stored in Redux so it survives DriveView remounts
   * during folder navigation.
   */
  drawer: {
    /** User clicked the X button — sheet stays hidden until a new upload starts. */
    userDismissed: boolean
    /** User collapsed the sheet — stays collapsed even when new files are added. */
    userMinimized: boolean
  }
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: UppyFileUploadState = {
  files: {},
  drawer: {
    userDismissed: false,
    userMinimized: false,
  },
}

// ============================================================================
// Slice
// ============================================================================

export const uppyFileUploadSlice = createSlice({
  name: 'uppyFileUploadState',
  initialState,
  reducers: {
    // ============== Client File Lifecycle ==============

    /** Added before Uppy while client-side validation runs; not eligible for init-upload. */
    fileValidationStarted: (
      state,
      action: PayloadAction<{
        clientTmpId: string
        filename: string
        fileSize: number
        folderId: string
      }>
    ) => {
      const { clientTmpId, filename, fileSize, folderId } = action.payload
      state.files[clientTmpId] = {
        client: {
          uppyFileId: '',
          clientTmpId,
          filename,
          fileSize,
          folderId,
          uploadStatus: 'validating',
          uploadProgress: 0,
        },
        backend: null,
      }
    },

    fileQueued: (
      state,
      action: PayloadAction<{
        uppyFileId: string
        clientTmpId: string
        filename: string
        fileSize: number
        folderId: string
      }>
    ) => {
      const { uppyFileId, clientTmpId, filename, fileSize, folderId } = action.payload
      state.files[clientTmpId] = {
        client: {
          uppyFileId,
          clientTmpId,
          filename,
          fileSize,
          folderId,
          uploadStatus: 'queued',
          uploadProgress: 0,
        },
        backend: null,
      }
    },

    /** Rejected before Uppy (wrong type); shown inline in the upload drawer, no upload started. */
    unsupportedTypeFileAdded: (
      state,
      action: PayloadAction<{
        clientTmpId: string
        filename: string
        fileSize: number
        folderId: string
      }>
    ) => {
      const { clientTmpId, filename, fileSize, folderId } = action.payload
      state.files[clientTmpId] = {
        client: {
          uppyFileId: '',
          clientTmpId,
          filename,
          fileSize,
          folderId,
          uploadStatus: 'unsupported_type',
          uploadProgress: 0,
        },
        backend: null,
      }
    },

    /** Rejected before init-upload; shown inline in the upload drawer, no upload started. */
    validationFailedFileAdded: (
      state,
      action: PayloadAction<{
        clientTmpId: string
        filename: string
        fileSize: number
        folderId: string
        error: string
      }>
    ) => {
      const { clientTmpId, filename, fileSize, folderId, error } = action.payload
      state.files[clientTmpId] = {
        client: {
          uppyFileId: '',
          clientTmpId,
          filename,
          fileSize,
          folderId,
          uploadStatus: 'error',
          uploadProgress: 0,
          uploadError: error,
        },
        backend: null,
      }
    },

    fileInitializing: (state, action: PayloadAction<{ clientTmpId: string }>) => {
      const file = state.files[action.payload.clientTmpId]
      if (file) {
        file.client.uploadStatus = 'initializing'
      }
    },

    fileInitialized: (
      state,
      action: PayloadAction<{
        clientTmpId: string
        serverFileId: string
        storagePath: string
      }>
    ) => {
      const { clientTmpId, serverFileId, storagePath } = action.payload
      const file = state.files[clientTmpId]
      if (file) {
        file.client.serverFileId = serverFileId
        file.client.storagePath = storagePath
      }
    },

    uploadStarted: (state, action: PayloadAction<{ clientTmpId: string }>) => {
      const file = state.files[action.payload.clientTmpId]
      if (file) {
        file.client.uploadStatus = 'uploading'
        file.client.uploadProgress = 0
      }
    },

    uploadProgress: (
      state,
      action: PayloadAction<{
        clientTmpId: string
        progress: number
      }>
    ) => {
      const file = state.files[action.payload.clientTmpId]
      if (file) {
        file.client.uploadProgress = action.payload.progress
      }
    },

    uploadCompleted: (state, action: PayloadAction<{ clientTmpId: string }>) => {
      const file = state.files[action.payload.clientTmpId]
      if (file) {
        file.client.uploadStatus = 'uploaded'
        file.client.uploadProgress = 100
      }
    },

    uploadFailed: (
      state,
      action: PayloadAction<{
        clientTmpId: string
        error: string
      }>
    ) => {
      const file = state.files[action.payload.clientTmpId]
      if (file) {
        file.client.uploadStatus = 'error'
        file.client.uploadError = action.payload.error
      }
    },

    // ============== Backend Data Updates ==============

    /**
     * Receives full backend data from the unified endpoint.
     * Used for both initial fetch and polling updates.
     */
    backendDataReceived: (
      state,
      action: PayloadAction<{
        serverFileId: string
        record: UserFileRead
        reductoJob: ReductoJobSafeRead | null
        assets: UserFileAssetRead[]
      }>
    ) => {
      const { serverFileId, record, reductoJob, assets } = action.payload
      // Find file by serverFileId
      const file = Object.values(state.files).find((f) => f.client.serverFileId === serverFileId)
      if (file) {
        file.backend = {
          record,
          reductoJob,
          assets,
        }
      }
    },

    /**
     * Marks a file as errored when the backend omits its ID from the batch response.
     *
     * The backend contract explicitly omits missing IDs rather than returning null entries.
     * If we keep polling forever, the UI will get stuck in an infinite loop.
     */
    backendDataMissing: (
      state,
      action: PayloadAction<{
        serverFileId: string
        error: string
      }>
    ) => {
      const { serverFileId, error } = action.payload
      const file = Object.values(state.files).find((f) => f.client.serverFileId === serverFileId)
      if (file) {
        file.client.uploadStatus = 'error'
        file.client.uploadError = error
      }
    },

    // ============== File Management ==============

    fileRemoved: (state, action: PayloadAction<{ clientTmpId: string }>) => {
      delete state.files[action.payload.clientTmpId]
    },

    filesRemovedByServerFileIds: (state, action: PayloadAction<{ serverFileIds: string[] }>) => {
      const serverFileIds = new Set(action.payload.serverFileIds)
      for (const [clientTmpId, file] of Object.entries(state.files)) {
        if (file.client.serverFileId && serverFileIds.has(file.client.serverFileId)) {
          delete state.files[clientTmpId]
        }
      }
    },

    filesMovedByServerFileIds: (state, action: PayloadAction<{ serverFileIds: string[]; folderId: string }>) => {
      const serverFileIds = new Set(action.payload.serverFileIds)
      for (const file of Object.values(state.files)) {
        if (file.client.serverFileId && serverFileIds.has(file.client.serverFileId)) {
          file.client.folderId = action.payload.folderId
        }
      }
    },

    fileRenamedByServerRecord: (state, action: PayloadAction<UserFileRead>) => {
      const file = Object.values(state.files).find((file) => file.client.serverFileId === action.payload.id)
      if (file) {
        file.client.filename = action.payload.original_filename
        file.client.folderId = action.payload.folder_id
        if (file.backend) {
          file.backend.record = action.payload
        }
      }
    },

    // ============== Upload Drawer UI ==============

    /** User expanded or collapsed the drawer. */
    drawerExpandedChanged: (state, action: PayloadAction<boolean>) => {
      state.drawer.userMinimized = !action.payload
    },

    /** User clicked the X / close button. */
    drawerDismissed: (state) => {
      state.drawer.userDismissed = true
    },

    /**
     * New files were added to the upload queue.
     * Resets dismissed/minimised states and expands the drawer, unless the user
     * had only minimised (not dismissed) — in that case the drawer stays collapsed.
     */
    drawerNewFilesAdded: (state) => {
      if (state.drawer.userDismissed) {
        // Coming back from a full dismiss: show expanded (userMinimized = false → !userMinimized = expanded).
        state.drawer.userDismissed = false
        state.drawer.userMinimized = false
      }
      // If userMinimized (but not dismissed): stay collapsed, just let it show.
    },
  },
})

// ============================================================================
// Exports
// ============================================================================

export const UppyFileUploadActions = uppyFileUploadSlice.actions
export default uppyFileUploadSlice.reducer
